import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import {
  clearAppUnlockSession,
  getAppUnlockToken,
  setAppUnlockSession,
} from '../lib/appUnlockStorage';

const AppLockContext = createContext(null);

export const useAppLock = () => {
  const context = useContext(AppLockContext);
  if (!context) {
    throw new Error('useAppLock must be used within AppLockProvider');
  }
  return context;
};

export const getStoredUnlockToken = () => getAppUnlockToken();

export const AppLockProvider = ({ children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [guardEnabled, setGuardEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const token = getAppUnlockToken();
      const headers = {};
      if (token) {
        headers['X-App-Unlock-Token'] = token;
      }
      const res = await fetch(`${API_URL}/app/status`, { headers });
      const data = await res.json();
      setGuardEnabled(Boolean(data.requires_unlock));
      if (!data.requires_unlock) {
        setUnlocked(true);
        return;
      }
      if (data.admin_unlocked) {
        setUnlocked(true);
        return;
      }
      if (token) {
        clearAppUnlockSession();
      }
      setUnlocked(false);
    } catch {
      // If status cannot be loaded, assume guard is on so admin APIs are not called without unlock.
      setGuardEnabled(true);
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'app_unlock_token' || event.key === 'app_unlock_expires_at') {
        checkStatus();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [checkStatus]);

  const unlock = async (passphrase) => {
    setUnlocking(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/app/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase: passphrase.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Invalid security passphrase');
        return false;
      }
      const minutes = data.expires_in_minutes || 480;
      const expiresAt = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      setAppUnlockSession(data.unlock_token, expiresAt);
      setUnlocked(true);
      return true;
    } catch {
      setError('Could not reach the server. Check your connection.');
      return false;
    } finally {
      setUnlocking(false);
    }
  };

  const lock = () => {
    clearAppUnlockSession();
    setUnlocked(false);
  };

  return (
    <AppLockContext.Provider
      value={{
        unlocked,
        guardEnabled,
        loading,
        error,
        unlocking,
        unlock,
        lock,
        checkStatus,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
};

export default AppLockProvider;
